import { useEffect, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '@core/store';
import { setCartOpen } from '@core/store/slices/uiSlice';
import { removeFromCart, updateQuantity } from '@core/store/slices/cartSlice';
import { getImageUrl } from '@shared/utils/helpers';
import { cn } from '@shared/utils';
import { useNavigate } from 'react-router-dom';

/**
 * 100% PARITY VERSION: Replicating Angular cart-sidebar exactly
 * Matches SVGs, timings (300ms), labels, and discount logic
 */
export function CartSidebar() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { isCartOpen } = useAppSelector((state) => state.ui);
    const { items: cartItems } = useAppSelector((state) => state.cart);

    const closeSidebar = () => dispatch(setCartOpen(false));

    // Parity: Handle body scroll lock
    useEffect(() => {
        if (isCartOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isCartOpen]);

    // Financial calculations parity with Angular cart.model
    const stats = useMemo(() => {
        const subtotal = cartItems.reduce((acc, item) => acc + (item.originalPrice || item.price) * item.quantity, 0);
        const total = cartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
        const savings = cartItems.reduce((acc, item) => acc + (item.discountAmount || 0) * item.quantity, 0);
        return { subtotal, total, savings };
    }, [cartItems]);

    const handleCheckout = () => {
        closeSidebar();
        navigate('/checkout');
    };

    return (
        <>
            {/* Overlay Parity (fadeIn animation implicit via visibility) */}
            {isCartOpen && (
                <div
                    className="cart-overlay fixed inset-0 bg-black/40 z-[999] cursor-pointer animate-in fade-in duration-200"
                    onClick={closeSidebar}
                />
            )}

            {/* Sidebar Drawer Parity (slideIn 300ms) */}
            <aside
                className={cn(
                    "cart-sidebar fixed top-0 right-0 h-screen w-full max-w-[520px] bg-white shadow-2xl z-[1000] flex flex-col overflow-hidden transition-transform duration-300 ease-out",
                    isCartOpen ? "translate-x-0" : "translate-x-full"
                )}
            >
                {/* Header Parity with exact SVG icons */}
                <header className="cart-header flex items-center justify-between px-6 py-5 border-b border-gray-200 bg-white shrink-0">
                    <div className="header-title flex items-center gap-3">
                        <svg className="bag-icon w-[26px] h-[26px] text-[#C4A661]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                            <line x1="3" y1="6" x2="21" y2="6"></line>
                            <path d="M16 10a4 4 0 0 1-8 0"></path>
                        </svg>
                        <h2 className="font-serif-lux text-[22px] font-normal text-gray-900">Shopping Cart</h2>
                    </div>
                    <button onClick={closeSidebar} className="close-btn w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-all text-gray-400 hover:text-gray-900">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </header>

                {/* Content Area Parity */}
                <div className="cart-content flex-1 overflow-y-auto p-4 md:p-5 bg-[#FAFAF9]">
                    {cartItems.length === 0 ? (
                        <div className="empty-state h-full flex flex-col items-center justify-center text-center p-8">
                            <div className="empty-icon w-[70px] h-[70px] mb-5 text-[#C4A661]/30">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                                    <line x1="3" y1="6" x2="21" y2="6"></line>
                                    <path d="M16 10a4 4 0 0 1-8 0"></path>
                                </svg>
                            </div>
                            <h3 className="font-serif-lux text-xl font-medium text-gray-900 mb-2">Your cart is empty</h3>
                            <p className="text-[13px] text-gray-500 mb-6 max-w-[200px] leading-relaxed">
                                Discover our exquisite lifestyle collection and find your perfect piece
                            </p>
                            <button onClick={closeSidebar} className="explore-btn bg-[#C4A661] hover:bg-[#B8944D] text-white px-8 py-3 rounded-lg text-sm font-medium transition-all hover:translate-y-[-2px] shadow-lg shadow-[#C4A661]/20">
                                Explore Collection
                            </button>
                        </div>
                    ) : (
                        <div className="cart-items flex flex-col gap-2">
                            {cartItems.map((item) => (
                                <article key={`${item.productId}-${item.variantId || ''}`} className="cart-item group relative flex gap-3 p-3 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-all">
                                    {/* Remove Button Parity (Gold-to-Red Hover Gradient) */}
                                    <button
                                        onClick={() => dispatch(removeFromCart({ productId: item.productId, variantId: item.variantId }))}
                                        className="remove-btn absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center bg-gradient-to-br from-[#C4A661] to-[#B8944D] rounded-full text-white border-2 border-white shadow-lg hover:from-red-500 hover:to-red-600 transition-all hover:scale-110"
                                    >
                                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <line x1="18" y1="6" x2="6" y2="18"></line>
                                            <line x1="6" y1="6" x2="18" y2="18"></line>
                                        </svg>
                                    </button>

                                    {/* Product Image Parity */}
                                    <div className="item-image w-[70px] h-[70px] shrink-0 rounded-lg overflow-hidden bg-gray-50 border border-gray-200">
                                        <img
                                            src={getImageUrl(item.productImage)}
                                            alt={item.productName}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                    </div>

                                    {/* Item Details Parity with accurate text */}
                                    <div className="item-details flex-1 min-w-0 pr-8">
                                        {item.categoryName && (
                                            <span className="category-badge inline-block bg-[#F5EFE7] text-[#8B7355] text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wider mb-1">
                                                {item.categoryName}
                                            </span>
                                        )}
                                        <h4 className="item-name font-serif-lux text-[15px] font-semibold text-gray-900 leading-tight line-clamp-2 mb-1">
                                            {item.productName}
                                        </h4>
                                        <div className="flex flex-wrap gap-1 mt-1 text-[11px] text-gray-500">
                                            {item.colorName && (
                                                <span className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">Color: {item.colorName}</span>
                                            )}
                                            {item.sizeName && (
                                                <span className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">Size: {item.sizeName}</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Right Column Parity (Qty Controls + Stacked Price) */}
                                    <div className="item-right-column flex flex-col items-end justify-between ml-auto">
                                        <div className="quantity-controls flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white">
                                            <button
                                                onClick={() => dispatch(updateQuantity({ productId: item.productId, variantId: item.variantId, quantity: item.quantity - 1 }))}
                                                disabled={item.quantity <= 1}
                                                className={cn("qty-btn w-7 h-7 flex items-center justify-center hover:bg-gray-50 transition-colors", item.quantity <= 1 && "opacity-30 cursor-not-allowed")}
                                            >
                                                <svg className="w-2.5 h-2.5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                                </svg>
                                            </button>
                                            <span className="qty-value w-8 text-center text-[13px] font-medium text-gray-900 border-x border-gray-200 py-1">
                                                {item.quantity}
                                            </span>
                                            <button
                                                onClick={() => dispatch(updateQuantity({ productId: item.productId, variantId: item.variantId, quantity: item.quantity + 1 }))}
                                                disabled={item.quantity >= (item.availableStockInventory || 100)}
                                                className={cn("qty-btn w-7 h-7 flex items-center justify-center hover:bg-gray-50 transition-colors", item.quantity >= (item.availableStockInventory || 100) && "opacity-30 cursor-not-allowed")}
                                            >
                                                <svg className="w-2.5 h-2.5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                                </svg>
                                            </button>
                                        </div>
                                        <div className="price-stack text-right mt-2">
                                            {item.discountAmount && item.discountAmount > 0 && (
                                                <span className="original-price block text-[10px] text-gray-400 line-through">
                                                    ${((item.originalPrice || item.price) * item.quantity).toFixed(2)}
                                                </span>
                                            )}
                                            <span className={cn("item-price font-serif-lux text-xl font-semibold", item.discountAmount && item.discountAmount > 0 ? "text-green-600" : "text-[#C4A661]")}>
                                                ${(item.price * item.quantity).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer Section Parity with accurate labels */}
                {cartItems.length > 0 && (
                    <footer className="cart-footer p-6 border-t border-gray-200 bg-white shrink-0">
                        <div className="order-summary mb-5">
                            <div className="summary-row flex justify-between items-center mb-2">
                                <span className="summary-label text-[13px] text-gray-500">Subtotal</span>
                                <span className="summary-value text-[13px] font-medium text-gray-900">${stats.subtotal.toFixed(2)}</span>
                            </div>
                            {stats.savings > 0 && (
                                <div className="summary-row flex justify-between items-center mb-2">
                                    <span className="summary-label text-[13px] text-gray-500">Total Savings</span>
                                    <span className="summary-value text-[13px] font-bold text-green-600">-${stats.savings.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="summary-row flex justify-between items-center mb-2">
                                <span className="summary-label text-[13px] text-gray-500">Shipping</span>
                                <span className="summary-note text-[12px] italic text-[#C4A661]">Calculated at checkout</span>
                            </div>
                            <div className="summary-divider h-px bg-gray-200 my-4" />
                            <div className="summary-row total-row flex justify-between items-baseline">
                                <span className="total-label font-serif-lux text-lg font-medium text-gray-900">Total</span>
                                <span className="total-value font-serif-lux text-[28px] font-semibold text-[#C4A661]">${stats.total.toFixed(2)}</span>
                            </div>
                        </div>

                        <button onClick={handleCheckout} className="checkout-btn w-full h-[48px] flex items-center justify-center gap-2.5 bg-[#C4A661] hover:bg-[#B8944D] text-white text-sm font-semibold rounded-lg transition-all hover:translate-y-[-2px] hover:shadow-lg hover:shadow-[#C4A661]/30 group mb-4">
                            <span>Proceed to Checkout</span>
                            <svg className="w-[15px] h-[15px] transition-transform group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                                <polyline points="12 5 19 12 12 19"></polyline>
                            </svg>
                        </button>

                        <div className="secure-badge flex items-center justify-center gap-2 mb-3">
                            <svg className="lock-icon w-3.5 h-3.5 text-[#C4A661]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                            <div className="secure-text flex items-center gap-1.5">
                                <span className="secure-title text-xs font-medium text-gray-900">Secure Checkout</span>
                                <span className="secure-subtitle text-[10px] text-[#C4A661]">Encrypted & Safe</span>
                            </div>
                        </div>

                        <button onClick={closeSidebar} className="continue-btn w-full text-[13px] text-gray-500 hover:text-gray-900 transition-colors py-2">
                            Continue Shopping
                        </button>
                    </footer>
                )}
            </aside>
        </>
    );
}

export default CartSidebar;
